import { useComputed } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { ACCESS_LABELS, FAC_LABELS, MEAL_ORDER, PERIODS, PERIOD_COLORS, TYPE_GROUPS, dishLabel, kindLabel, mealLabel } from "../data/layers";
import type { FoodPoint, LayerId, LayerPoint } from "../types";
import {
  access, activeFilterCount, centerMode, cities, clearFilters, counties, countyCounts, dateFrom, dateTo, dishes, enabledLayers, equineData, eventWindowSet, eventsInWindow, eventsOpen, facilities, featuresByLayer, filteredAll, filtersOpen,
  closedLinesOn, fixturesData, followMap, freeOnly, kinds, liveTrainsCount, liveTrainsError, liveTrainsOn, meals, myLocation, noLivestock, periods, radiusKm, railLinesOn, saved, savedOnly, toggleInSet, toggleKind, typeGroups, wheelchair,
} from "../state/store";
import { DateWindow, windowLabel } from "./DateWindow";
import { buildGpx, buildKml, download, stamp } from "../utils/export";
import { bboxOf } from "../utils/geo";
import { focusBBox } from "../state/store";
import { Timeline } from "./Timeline";

function Section({ title, children, defaultOpen = true, badge }: { title: string; children: ComponentChildren; defaultOpen?: boolean; badge?: number }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section class={`fsection ${open ? "is-open" : ""}`}>
      <button type="button" class="fsection-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>{title}</span>
        {badge ? <span class="badge">{badge}</span> : null}
        <span class="chev" aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      {open && <div class="fsection-body">{children}</div>}
    </section>
  );
}

function Chip({ on, label, color, onClick, count }: { on: boolean; label: string; color?: string; onClick: () => void; count?: number }) {
  return (
    <button type="button" class={`chip ${on ? "is-on" : ""}`} style={color ? { "--chip": color } : undefined} aria-pressed={on} onClick={onClick}>
      {color && <span class="chip-dot" aria-hidden="true" />}
      {label}
      {count != null && <span class="chip-count">{count}</span>}
    </button>
  );
}

function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <label class="toggle">
      <input type="checkbox" checked={on} onChange={(e) => onChange((e.target as HTMLInputElement).checked)} />
      <span>{label}</span>
    </label>
  );
}

/** Distinct kinds (and dishes) present in a layer, with counts, for data-driven chips. */
function useKindCounts(layer: LayerId, field: "kinds" | "dishes" | "meal") {
  return useComputed(() => {
    const m = new Map<string, number>();
    for (const f of featuresByLayer.value[layer]) {
      const arr = ((f.raw as LayerPoint & FoodPoint)[field] as string[] | undefined) ?? [];
      for (const k of arr) m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }).value;
}

function WhereSection() {
  const c = centerMode.value;
  const [locating, setLocating] = useState(false);
  const useMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        myLocation.value = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        centerMode.value = { kind: "me", lat: pos.coords.latitude, lon: pos.coords.longitude };
        if (!radiusKm.value) radiusKm.value = 25;
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  };
  return (
    <Section title="Where">
      <div class="row-2">
        <input type="number" min={1} max={500} step={5} placeholder="km" value={radiusKm.value ?? ""} aria-label="Radius in km"
          onInput={(e) => { const v = (e.target as HTMLInputElement).value; radiusKm.value = v === "" ? null : Number(v); }} />
        <span class="sep">of</span>
        <select aria-label="Centre town" value={c?.kind === "me" ? "me" : c?.kind === "city" ? String(c.idx) : ""}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            if (v === "") centerMode.value = null;
            else if (v === "me") useMe();
            else centerMode.value = { kind: "city", idx: Number(v) };
            if (v !== "" && !radiusKm.value) radiusKm.value = 25;
          }}>
          <option value="">— anywhere —</option>
          {myLocation.value && <option value="me">📍 Your location</option>}
          {cities.value.map((city, i) => <option value={String(i)}>{city.name}</option>)}
        </select>
      </div>
      <button type="button" class="btn-ghost btn-here" onClick={useMe} disabled={locating}>
        📍 {locating ? "Locating…" : "Use my location"}
      </button>
      <Toggle on={followMap.value} label="List follows the map view" onChange={(v) => { followMap.value = v; }} />
      <CountyChips />
    </Section>
  );
}

function CountyChips() {
  const cc = countyCounts.value;
  if (!cc.length) return null;
  const pick = (c: string) => {
    toggleInSet(counties, c);
    // Frame the selection: a county filter is a "take me there" gesture.
    if (counties.value.size) {
      const b = bboxOf(filteredAll.value.map((x) => x.f));
      if (b) focusBBox(b);
    }
  };
  return (
    <>
      <div class="flabel">County</div>
      <div class="chips chips-dense">
        {cc.map(([c, n]) => <Chip on={counties.value.has(c)} label={c} count={n} onClick={() => pick(c)} />)}
      </div>
    </>
  );
}

function HistorySection() {
  return (
    <>
      <Section title="When" badge={periods.value.size + (dateFrom.value != null || dateTo.value != null ? 1 : 0)}>
        <Timeline />
        <div class="chips">
          {PERIODS.map(([id, label]) => (
            <Chip on={periods.value.has(id)} label={label} color={PERIOD_COLORS[id]} onClick={() => toggleInSet(periods, id)} />
          ))}
        </div>
      </Section>
      <Section title="What kind of place" badge={typeGroups.value.size}>
        <div class="chips">
          {TYPE_GROUPS.map((g) => <Chip on={typeGroups.value.has(g.id)} label={g.label} onClick={() => toggleInSet(typeGroups, g.id)} />)}
        </div>
      </Section>
      <Section title="Effort & facilities" badge={access.value.size + facilities.value.size + (noLivestock.value ? 1 : 0) + (freeOnly.value ? 1 : 0) + (wheelchair.value ? 1 : 0)}>
        <div class="chips">
          {Object.entries(ACCESS_LABELS).map(([id, label]) => <Chip on={access.value.has(id)} label={label} onClick={() => toggleInSet(access, id)} />)}
        </div>
        <div class="chips">
          {Object.entries(FAC_LABELS).map(([id, label]) => <Chip on={facilities.value.has(id)} label={label} onClick={() => toggleInSet(facilities, id)} />)}
        </div>
        <Toggle on={noLivestock.value} label="No livestock in the field" onChange={(v) => { noLivestock.value = v; }} />
        <Toggle on={freeOnly.value} label="Free entry" onChange={(v) => { freeOnly.value = v; }} />
        <Toggle on={wheelchair.value} label="Wheelchair accessible" onChange={(v) => { wheelchair.value = v; }} />
      </Section>
    </>
  );
}
function KindSection({ layer, title }: { layer: LayerId; title: string }) {
  const kc = useKindCounts(layer, "kinds");
  const sel = kinds.value[layer] ?? new Set<string>();
  if (!kc.length) return null;
  return (
    <Section title={title} badge={sel.size + (layer === "food" ? dishes.value.size + meals.value.size : 0)}>
      {layer === "food" && <MealChips />}
      {layer === "food" && <div class="flabel">Kind of place</div>}
      <div class="chips">
        {kc.map(([k, n]) => <Chip on={sel.has(k)} label={kindLabel(k)} count={n} onClick={() => toggleKind(layer, k)} />)}
      </div>
      {layer === "food" && <DishChips />}
      {layer === "trains" && <TrainOverlays />}
    </Section>
  );
}

function TrainOverlays() {
  return (
    <>
      <div class="flabel">On the map</div>
      <Toggle on={railLinesOn.value} label="Show the track" onChange={(v) => { railLinesOn.value = v; }} />
      <Toggle on={closedLinesOn.value} label="Show closed lines" onChange={(v) => { closedLinesOn.value = v; }} />
      {closedLinesOn.value && (
        <p class="hint">
          The network Ireland used to have — the West Clare, the Harcourt Street line, the GNR
          branches through Cavan and Monaghan. Solid-ish where the rails are still in the ground,
          faint where only the trackbed survives (often a greenway now).
        </p>
      )}
      <LiveTrainsToggle />
    </>
  );
}

function LiveTrainsToggle() {
  const on = liveTrainsOn.value;
  const n = liveTrainsCount.value;
  const err = liveTrainsError.value;
  return (
    <>
      <div class="flabel">Live</div>
      <Toggle on={on} label="Show running trains" onChange={(v) => { liveTrainsOn.value = v; }} />
      {on && (
        <p class="hint">
          {err ? `Live feed unavailable right now (${err}).` : n ? `${n} train${n === 1 ? "" : "s"} running now, on Iarnród Éireann's network.` : "Loading live positions…"}
        </p>
      )}
    </>
  );
}

function MealChips() {
  const mc = useKindCounts("food", "meal");
  if (!mc.length) return null;
  const order = new Map(MEAL_ORDER.map((m, i) => [m, i]));
  const sorted = mc.slice().sort((a, b) => (order.get(a[0]) ?? 99) - (order.get(b[0]) ?? 99));
  return (
    <>
      <div class="flabel">Meal</div>
      <div class="chips">
        {sorted.map(([m, n]) => <Chip on={meals.value.has(m)} label={mealLabel(m)} count={n} onClick={() => toggleInSet(meals, m)} />)}
      </div>
    </>
  );
}

function DishChips() {
  const dc = useKindCounts("food", "dishes");
  if (!dc.length) return null;
  return (
    <>
      <div class="flabel">Dishes</div>
      <div class="chips">
        {dc.map(([d, n]) => <Chip on={dishes.value.has(d)} label={dishLabel(d)} count={n} onClick={() => toggleInSet(dishes, d)} />)}
      </div>
    </>
  );
}

function SavedSection() {
  const n = saved.value.size;
  const exportSet = () => {
    const all = filteredAll.value.map((x) => x.f);
    return n ? all.filter((f) => saved.value.has(f.key)) : all;
  };
  const doExport = (kind: "gpx" | "kml") => {
    const feats = exportSet();
    if (!feats.length) { alert("Nothing to export — star some places or widen the filters."); return; }
    const source = n ? "starred" : "filtered";
    if (kind === "gpx") download(buildGpx(feats), `ireland-${source}-${stamp()}.gpx`, "application/gpx+xml");
    else download(buildKml(feats), `ireland-${source}-${stamp()}.kml`, "application/vnd.google-earth.kml+xml");
  };
  return (
    <Section title="Starred & export" defaultOpen={false} badge={savedOnly.value ? 1 : 0}>
      <Toggle on={savedOnly.value} label={`Only starred${n ? ` (${n})` : ""}`} onChange={(v) => { savedOnly.value = v; }} />
      <p class="hint">{n ? `Exports your ${n} starred place${n === 1 ? "" : "s"} (with parking waypoints).` : "Nothing starred yet — export uses everything matching the filters."}</p>
      <div class="row-btns">
        <button type="button" class="btn-ghost" onClick={() => doExport("gpx")}>Download GPX</button>
        <button type="button" class="btn-ghost" onClick={() => doExport("kml")}>Download KML</button>
      </div>
    </Section>
  );
}

/** The trip window, and the What's on list it feeds. Every dated feed reads it. */
function WhenSection() {
  const n = eventsInWindow.value.length;
  const loaded = fixturesData.value || equineData.value;
  return (
    <Section title="When you're there" badge={(eventWindowSet.value ? 1 : 0) + (eventsOpen.value ? 1 : 0)}>
      <p class="hint">Set your dates and the map keeps only what is on then: Gaelic matches, race meetings, point-to-points and horse trials. Venues with something on are ringed.</p>
      <DateWindow />
      <Toggle on={eventsOpen.value} label={`Show what's on${loaded ? ` (${n})` : ""}`} onChange={(v) => { eventsOpen.value = v; }} />
      {!loaded && <p class="hint">Fixtures are loading…</p>}
      {loaded && eventWindowSet.value && <p class="hint">{n} {n === 1 ? "thing" : "things"} on {windowLabel()}.</p>}
    </Section>
  );
}

export function FilterPanel() {
  const on = enabledLayers.value;
  const n = activeFilterCount.value;
  return (
    <div class={`filters ${filtersOpen.value ? "is-open" : ""}`} id="filters">
      <div class="filters-head">
        <h2>Filters {n ? <span class="badge">{n}</span> : null}</h2>
        {n ? <button type="button" class="btn-link" onClick={clearFilters}>Clear all</button> : null}
        <button type="button" class="btn-link filters-close" onClick={() => { filtersOpen.value = false; }}>Done</button>
      </div>
      <WhereSection />
      {on.has("history") && <HistorySection />}
      {on.has("nature") && <KindSection layer="nature" title="Land & sea" />}
      {on.has("gaa") && <KindSection layer="gaa" title="GAA" />}
      {on.has("equine") && <KindSection layer="equine" title="Horses" />}
      {(on.has("gaa") || on.has("equine")) && <WhenSection />}
      {on.has("food") && <KindSection layer="food" title="Eat local" />}
      {on.has("trains") && <KindSection layer="trains" title="Trains" />}
      <SavedSection />
    </div>
  );
}
